import React, { useEffect, useRef, useState } from 'react'
import { Badge, Button, Form, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { IoCalendarOutline } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'
import ModalNovoAgendamento from '../../components/Modals/ModalNovoAgendamento'
import { SweetAlert } from '../../components/sweetalert'
import { URL } from '../../config/url'
import Agendamentos from '../../services/Agendamentos'
import Status from '../../services/Status'
import {
  agendamentoFormValues, agendamentoSearchFilter, clinicTime, joinDetail, progressText, statusTextColor,
} from '../../utilities/dashboard'
import ActionList, { PacienteButton, ProntuarioButton } from './ActionList'

const noop = () => { }
const alertText = (error) => error?.response?.data?.message ?? error?.message

// Próximos horários da aba Geral. O calendário de cada linha abre o agendamento no mesmo modal da Agenda
// (components/Modals/ModalNovoAgendamento), sem sair do Dashboard: um formulário só deste bloco, preenchido na regra do
// showInfoModal da Agenda, e a lista de status da mesma chamada de lá, pedida na primeira abertura.
// onChange: recarrega a aba (o modal salvou ou excluiu, ou o agendamento já não está na agenda)
const NextAgendamentos = ({ items, onChange }) => {
  const navigate = useNavigate()
  const { register, setValue, getValues, control } = useForm()
  const [show, setShow] = useState(false)
  const [status, setStatus] = useState([])
  const [openingId, setOpeningId] = useState(null)
  const statusRef = useRef(null)
  const openingRef = useRef(false)
  const mountedRef = useRef(false)
  const changeRef = useRef(onChange)
  changeRef.current = onChange

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Busca o agendamento na lista da Agenda (o paciente no dia do horário) e escolhe pelo id. Enquanto busca, o botão
  // mostra carregando e os outros ficam desabilitados
  async function openAgendamento(item) {
    if (openingRef.current) return
    openingRef.current = true
    setOpeningId(item.agendamento_id)

    const loadStatus = statusRef.current
      ? Promise.resolve(statusRef.current)
      : Status.listAll('', '', 300).then(({ data }) => data.data || [])
    const [statusResult, listResult] = await Promise.allSettled([loadStatus, Agendamentos.listAll(agendamentoSearchFilter(item))])

    openingRef.current = false
    if (!mountedRef.current) return
    setOpeningId(null)

    if (listResult.status === 'rejected') {
      SweetAlert.error(alertText(listResult.reason), 'Agendamento')
      return
    }

    const agendamento = (listResult.value?.data?.data || []).find((row) => row.id === item.agendamento_id)
    if (!agendamento) {
      SweetAlert.warn('Agendamento não encontrado: pode ter sido excluído ou remarcado. Atualizando a lista.', 'Agendamento')
      changeRef.current?.()
      return
    }

    if (statusResult.status === 'rejected') {
      SweetAlert.error(alertText(statusResult.reason), 'Status')
      return
    }

    statusRef.current = statusResult.value
    setStatus(statusResult.value)
    Object.entries(agendamentoFormValues(agendamento)).forEach(([name, value]) => setValue(name, value))
    setShow(true)
  }

  return (
    <ActionList
      title='Próximos horários'
      info='general_next'
      subtitle='de agora até o fim do dia, com o progresso do pacote'
      showCount={false}
      total={(items || []).length}
      items={items}
      emptyText='Nenhum horário restante hoje.'
      renderItem={(item) => {
        const opening = openingId === item.agendamento_id
        return {
          key: item.agendamento_id,
          lead: clinicTime(item.start),
          name: item.paciente_nome,
          detail: joinDetail(item.tecnico_nome, item.tipo, progressText(item.prontuario)),
          actions: (
            <>
              {item.status?.nome ?
                <Badge bg='' className='dashboard-status' style={{ backgroundColor: item.status.cor || '#6c757d', color: statusTextColor(item.status.cor || '#6c757d') }}>{item.status.nome}</Badge>
                : ''}
              <Button
                variant='outline-primary'
                size='sm'
                className={`dashboard-row-button dashboard-icon-button${opening ? ' disabled' : ''}`}
                aria-label='Abrir agendamento'
                title='Abrir agendamento'
                aria-busy={opening}
                // O clicado fica focável (só parece desabilitado), para o Modal devolver o foco a ele ao fechar; o openingRef barra o 2º pedido
                aria-disabled={opening || undefined}
                disabled={Boolean(openingId) && !opening}
                onClick={() => openAgendamento(item)}
              >
                {opening ? <Spinner animation='border' size='sm' aria-hidden='true' /> : <IoCalendarOutline aria-hidden='true' />}
              </Button>
              <ProntuarioButton prontuarioId={item.prontuario?.id} latestProntuarioId={item.latest_prontuario_id} />
              <PacienteButton pacienteId={item.paciente_id} />
            </>
          ),
        }
      }}
      footer={
        // O modal da Agenda dentro de um Form, como lá (o conteúdo vai para o body pelo portal do Modal)
        <Form>
          <ModalNovoAgendamento
            show={show}
            handleClose={() => setShow(false)}
            setAgendamentos={noop}
            register={register}
            setValue={setValue}
            getValues={getValues}
            listAll={() => changeRef.current?.()}
            control={control}
            status={status}
            goToDate={() => navigate(URL.calendario)}
          />
        </Form>
      }
    />
  )
}

export default NextAgendamentos
